import { GlobalNotificationStatus } from "@nova/component-global-notification"
import {
    GroupType,
    JobStatusType,
    SidePanelLayerOrGroup,
    SidePanelLayerType,
} from "@nova/constants"
import { ContextMenuItem } from "features/data-management/workspace/TODO-put-in-package/ContextMenu"
import { selectNextGroupNumber } from "features/entity-caching/groups/selectors"
import { createGroup } from "features/pubsub/entityActions/cubes/groups"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useCallback, useEffect, useMemo } from "react"
import { selectUserID } from "user/profile/selectors"
import { setGlobalNotificationContext } from "user/profile/slice"
import {
    clearSelection,
    selectLastSelectedLayer,
    selectSelectedLayerIds,
    selectSelectedLayers,
    setRenamingLayerId,
} from "../slice.new"
import * as Icons from "@nova/component-icons"
import { sum } from "nodash"
import { dataSelectorsByMode, maxDepthByMode, SidePanelMode } from "../constants"
import { initializeCenterModalForComponentAction } from "features/content-overlay/slice"
import { ModalNames } from "features/modals/types"
import { showGlobalModal } from "runlevel/actions"
import {
    selectAllEntitiesMap,
    selectCanDownload,
    selectChildrenEntityIds,
    selectPdfEntitiesMap,
    setCanDownload,
} from "../slice"
import { selectPDFJobs } from "plugins/PDFv2/PDFSlice"
import {
    getGenericConfirmationModalComponent,
    useConfirmationModalContext,
} from "features/modals/WorkspaceConfirmationModalV2"
import store from "integrations/redux/store"
import { createNewLayerFractionalIndex } from "@nova/serializer/src/client/util/fractional_indexing"
import { RootState } from "integrations/redux"
import { useArchiveMultipleSelectedLayersContextMenuOption } from "./useArchiveContextMenuOption"
import { selectNonMergedInfraredFlightsMap } from "features/entity-caching/flights/selectors"
import { createMergedFlight } from "features/pubsub/entityActions/cubes/mergedFlights"
import {
    getSidePanelLayerTypeToDeleteFunctionMap,
    getSidePanelLayerTypeToUpdateFunctionMap,
} from "plugins/plugin-registry"
import { useDataTransfer } from "integrations/transfer/useDataTransfer"
import { v4 as randomUUID } from "uuid"

export const getNestedLayerCount = (entities: SidePanelLayerOrGroup[]): number => {
    if (!entities.length) return 0
    return (
        entities.length + sum(entities.map((entity) => getNestedLayerCount(entity.children || [])))
    )
}

export const getVisibleNestedLayerCount = (
    entities: SidePanelLayerOrGroup[],
    expandedLayerIds: string[]
): number => {
    if (!entities.length) return 0
    return (
        entities.length +
        sum(
            entities.map((entity) =>
                expandedLayerIds.includes(entity.id)
                    ? getVisibleNestedLayerCount(entity.children || [], expandedLayerIds)
                    : 0
            )
        )
    )
}

const areLayersSiblings = (a: SidePanelLayerOrGroup, b: SidePanelLayerOrGroup) => {
    if (!a.parentId || a.parentId === "null") {
        return !b.parentId || b.parentId === "null"
    }
    return a.parentId === b.parentId
}

export function useLayersMultipleSelectedContextMenuActions() {
    const dispatch = useAppDispatch()
    const userId = useAppSelector(selectUserID)
    const selectedLayers = useAppSelector(selectSelectedLayers)
    const { canDownload, download } = useExportSelectedShapes()
    const confirmationModal = useConfirmationModalContext()

    const { handleCreateGroup, canCreateGroupFromSelection } = useCreateGroup({
        mode: "Layers",
        groupType: GroupType.Layer,
    })

    const layerActions = useMemo(() => {
        const arr = [] as ContextMenuItem[]
        if (canCreateGroupFromSelection) {
            arr.push({
                title: "New Group",
                onClick: handleCreateGroup,
                Icon: Icons.Folder,
            })
        }
        if (canDownload) {
            arr.push({
                title: "Export Shapes",
                onClick: download,
                Icon: Icons.Download,
            })
        }
        const state = store.getState()
        const infraredFlightMap = selectNonMergedInfraredFlightsMap(state)
        if (
            selectedLayers.every(
                (layer) =>
                    layer.type === SidePanelLayerType.HotspotFlight &&
                    infraredFlightMap[layer.id] &&
                    !infraredFlightMap[layer.id].skippedProcessing &&
                    infraredFlightMap[layer.id].job?.status === JobStatusType.COMPLETED
            )
        ) {
            arr.push({
                title: "Merge Flights",
                onClick: () => {
                    dispatch(
                        createMergedFlight({
                            data: { createdBy: { userId: userId! } },
                            context: {
                                flightIds: selectedLayers.map((layer) => layer.id),
                                parentId: selectLastSelectedLayer(store.getState())?.parentId,
                            },
                        })
                    )
                },
                Icon: Icons.Merge,
            })
        }
        return arr
    }, [
        canCreateGroupFromSelection,
        canDownload,
        dispatch,
        download,
        handleCreateGroup,
        selectedLayers,
        userId,
    ])

    const mapActions = useMultipleSelectedMapActions()
    const archiveAction = useArchiveMultipleSelectedLayersContextMenuOption()

    const actions = useMemo(() => {
        const arr = [] as ContextMenuItem[][]
        if (layerActions.length) {
            arr.push(layerActions)
        }
        arr.push(mapActions)
        if (archiveAction) {
            arr.push([archiveAction])
        }
        arr.push([
            {
                title: `Delete ${getNestedLayerCount(selectedLayers)} Layers`,
                onClick: async () => {
                    const state = store.getState()
                    const selectedLayers = selectSelectedLayers(state)
                    const didConfirm = await confirmationModal.confirm({
                        RenderComponent: getGenericConfirmationModalComponent({
                            title: "Delete Selection?",
                            message: `This will delete ${getNestedLayerCount(selectedLayers)} layers, do you want to proceed?`,
                        }),
                    })
                    if (!didConfirm) return
                    const entityMap = selectAllEntitiesMap(state)
                    const entityIdsAndTypesToDelete = selectedLayers.map((layer) => ({
                        id: layer.id,
                        type: layer.type,
                    }))
                    entityIdsAndTypesToDelete.forEach(({ id }) => {
                        const childrenIds = selectChildrenEntityIds(id)(state)
                        childrenIds.forEach((childId) => {
                            entityIdsAndTypesToDelete.push({
                                id: childId,
                                type: entityMap[childId]?.type,
                            })
                        })
                    })
                    entityIdsAndTypesToDelete.forEach(({ id, type }) => {
                        if (!!id && !!type) {
                            dispatch(getSidePanelLayerTypeToDeleteFunctionMap()[type]({ id }))
                        }
                    })
                    dispatch(clearSelection())
                },
                Icon: Icons.Trash,
                isDestructive: true,
            },
        ])
        return arr
    }, [archiveAction, confirmationModal, dispatch, layerActions, mapActions, selectedLayers])

    return actions
}

export function useAssetsMultipleSelectedContextMenuActions() {
    const dispatch = useAppDispatch()
    const selectedLayerIds = useAppSelector(selectSelectedLayerIds)
    const { canDownload, download } = useExportSelectedShapes()
    const confirmationModal = useConfirmationModalContext()

    const nestedSelectedLayerIds = useMemo(() => {
        const nestedLayerIds = [...selectedLayerIds]
        const state = store.getState()
        const entityMap = selectAllEntitiesMap(state)
        selectedLayerIds.forEach((layerId) => {
            const entity = entityMap[layerId]
            if (entity && entity.children) {
                nestedLayerIds.push(...entity.children.map((child) => child.id))
            }
        })
        return nestedLayerIds
    }, [selectedLayerIds])

    const actions: ContextMenuItem[][] = []
    if (canDownload) {
        const layerActions: ContextMenuItem[] = []
        layerActions.push({
            title: "Export Shapes",
            onClick: download,
            Icon: Icons.Download,
        })
        actions.push(layerActions)
    }

    const mapActions = useMultipleSelectedMapActions()
    actions.push(mapActions)

    actions.push([
        {
            title: `Delete ${nestedSelectedLayerIds.length} Layers`,
            onClick: async () => {
                const didConfirm = await confirmationModal.confirm({
                    RenderComponent: getGenericConfirmationModalComponent({
                        title: "Delete Selection?",
                        message: `This will delete ${nestedSelectedLayerIds.length} layers, do you want to proceed?`,
                    }),
                })
                if (!didConfirm) return
                const state = store.getState()
                const entityMap = selectAllEntitiesMap(state)
                nestedSelectedLayerIds.forEach((layerId) => {
                    const entity = entityMap[layerId]
                    if (entity && entity.type) {
                        dispatch(
                            getSidePanelLayerTypeToDeleteFunctionMap()[entity.type]({ id: layerId })
                        )
                    }
                })
                dispatch(clearSelection())
            },
            Icon: Icons.Trash,
            isDestructive: true,
        },
    ])

    return actions
}

export function usePdfMultipleSelectedContextMenuActions() {
    const dispatch = useAppDispatch()
    const selectedLayerIds = useAppSelector(selectSelectedLayerIds)
    const pdfJobs = useAppSelector(selectPDFJobs)
    const confirmationModal = useConfirmationModalContext()

    const nestedSelectedLayerIds = useMemo(() => {
        const nestedLayerIds = [...selectedLayerIds]
        selectedLayerIds.forEach((layerId) => {
            const state = store.getState()
            const entityMap = selectPdfEntitiesMap(state)
            const entity = entityMap[layerId]
            if (!entity) return
            if (entity.type === SidePanelLayerType.Group) {
                nestedLayerIds.push(
                    ...pdfJobs.filter((job) => job.parentId === layerId).map((job) => job.id)
                )
            }
        })
        return nestedLayerIds
    }, [pdfJobs, selectedLayerIds])

    const { canCreateGroupFromSelection, handleCreateGroup } = useCreateGroup({
        mode: "PDFs",
        groupType: GroupType.PDF,
    })

    const { canTransfer, transfer, integrationName } = useDataTransfer()

    const actions: ContextMenuItem[][] = []
    const layerActions: ContextMenuItem[] = []
    if (canCreateGroupFromSelection) {
        layerActions.push({
            title: "New Group",
            onClick: handleCreateGroup,
            Icon: Icons.Folder,
        })
    }
    if (canTransfer) {
        layerActions.push({
            title: `Transfer to ${integrationName}`,
            onClick: () => {
                transfer("pdf", selectedLayerIds)
            },
            Icon: Icons.Backup,
        })
    }
    actions.push(layerActions)

    actions.push([
        {
            title: `Delete ${nestedSelectedLayerIds.length} Layers`,
            onClick: async () => {
                const didConfirm = await confirmationModal.confirm({
                    RenderComponent: getGenericConfirmationModalComponent({
                        title: "Delete Selection?",
                        message: `This will delete ${nestedSelectedLayerIds.length} layers, do you want to proceed?`,
                    }),
                })
                if (!didConfirm) return
                const state = store.getState()
                const entityMap = selectPdfEntitiesMap(state)
                nestedSelectedLayerIds.forEach((layerId) => {
                    const entity = entityMap[layerId]
                    if (entity && entity.type) {
                        dispatch(
                            getSidePanelLayerTypeToDeleteFunctionMap()[entity.type]({ id: layerId })
                        )
                    }
                })
                dispatch(clearSelection())
            },
            Icon: Icons.Trash,
            isDestructive: true,
        },
    ])

    return actions
}

export function useCreateGroup({ mode, groupType }: { mode: SidePanelMode; groupType: GroupType }) {
    const dispatch = useAppDispatch()
    const userId = useAppSelector(selectUserID)
    const selectedLayers = useAppSelector(selectSelectedLayers)
    const entityMap = useAppSelector(dataSelectorsByMode[mode].selectEntityMap)
    const nextGroupNumber = useAppSelector(selectNextGroupNumber)
    const getLayerDepth = useCallback(
        (id: string): number => {
            if (!entityMap[id]?.parentId || entityMap[id].parentId === "null") {
                return 0
            }
            return 1 + getLayerDepth(entityMap[id].parentId)
        },
        [entityMap]
    )
    const getChildrenDepth = useCallback(
        (id: string, depth = 0): number => {
            if (!entityMap[id]?.children?.length) {
                return depth
            }
            return Math.max(
                ...entityMap[id].children.map((child) => getChildrenDepth(child.id, depth + 1))
            )
        },
        [entityMap]
    )
    const selectionIsAllSiblings = useMemo(() => {
        for (let i = 1; i < selectedLayers.length; i++) {
            if (!areLayersSiblings(selectedLayers[i], selectedLayers[i - 1])) {
                return false
            }
        }
        return true
    }, [selectedLayers])
    const handleCreateGroup = useCallback(() => {
        if (!userId) {
            console.error("No userId when attempting to create a group.")
            return
        }
        if (selectedLayers.length < 1) {
            console.error("No layers selected while trying to group")
            return
        }

        const isTooNested = !selectedLayers.every((layer) => {
            const layerDepth = getLayerDepth(layer.id)
            const childrenDepth = getChildrenDepth(layer.id)
            return layerDepth + childrenDepth < maxDepthByMode[mode]
        })
        if (isTooNested) {
            console.error("Folders are too nested to group again")
            dispatch(
                setGlobalNotificationContext({
                    status: GlobalNotificationStatus.error,
                    message: `Groups may have a depth of ${maxDepthByMode[mode]}`,
                    timeoutInMs: 8000,
                })
            )
            return
        }

        if (!selectionIsAllSiblings) {
            console.error("Cannot group layers that are not siblings of each other.")
            return
        }

        const newId = randomUUID()
        dispatch(
            createGroup({
                data: {
                    name: `Group ${nextGroupNumber}`,
                    createdBy: { userId },
                    groupType,
                },
                context: {
                    id: newId,
                    index: selectedLayers[0].index,
                    parentId: selectedLayers[0].parentId,
                },
            })
        )
        dispatch(setRenamingLayerId({ id: newId }))
    }, [
        dispatch,
        getChildrenDepth,
        getLayerDepth,
        groupType,
        mode,
        nextGroupNumber,
        selectedLayers,
        selectionIsAllSiblings,
        userId,
    ])
    const canCreateGroupFromSelection = useMemo(
        () =>
            selectedLayers.reduce(
                (acc, layer) => Math.max(acc, getLayerDepth(layer.id) + getChildrenDepth(layer.id)),
                0
            ) < maxDepthByMode[mode] && selectionIsAllSiblings,
        [getChildrenDepth, getLayerDepth, mode, selectedLayers, selectionIsAllSiblings]
    )
    const handleCreateEmptyGroup = useCallback(() => {
        if (!userId) {
            console.error("No userId when attempting to create a group.")
            return
        }
        const state = store.getState() as RootState
        const newId = randomUUID()
        dispatch(
            createGroup({
                data: {
                    name: `Group ${nextGroupNumber}`,
                    createdBy: { userId },
                    groupType,
                },
                context: {
                    id: newId,
                    index: createNewLayerFractionalIndex(state.project.data),
                    parentId: undefined,
                    isEmpty: true,
                },
            })
        )
        dispatch(setRenamingLayerId({ id: newId }))
    }, [dispatch, groupType, nextGroupNumber, userId])
    return {
        canCreateGroupFromSelection,
        handleCreateGroup,
        handleCreateEmptyGroup,
    }
}

function useMultipleSelectedMapActions() {
    const dispatch = useAppDispatch()
    const selectedLayers = useAppSelector(selectSelectedLayers)
    const mapActions = useMemo(() => {
        const arr = [] as ContextMenuItem[]
        if (selectedLayers.every((layer) => layer.visible)) {
            arr.push({
                title: "Hide",
                onClick: () => {
                    selectedLayers.forEach((layer) => {
                        if (layer.type) {
                            dispatch(
                                getSidePanelLayerTypeToUpdateFunctionMap()[layer.type]({
                                    id: layer.id,
                                    updates: {
                                        visible: false,
                                    },
                                })
                            )
                        }
                    })
                },
                Icon: Icons.VisibilityEyeOff,
            })
        } else {
            arr.push({
                title: "Show",
                onClick: () => {
                    selectedLayers.forEach((layer) => {
                        if (layer.type) {
                            dispatch(
                                getSidePanelLayerTypeToUpdateFunctionMap()[layer.type]({
                                    id: layer.id,
                                    updates: {
                                        visible: true,
                                    },
                                })
                            )
                        }
                    })
                },
                Icon: Icons.VisibilityEye,
            })
        }
        if (selectedLayers.every((layer) => layer.locked)) {
            arr.push({
                title: "Unlock",
                onClick: () => {
                    selectedLayers.forEach((layer) => {
                        if (layer.type) {
                            dispatch(
                                getSidePanelLayerTypeToUpdateFunctionMap()[layer.type]({
                                    id: layer.id,
                                    updates: {
                                        locked: false,
                                    },
                                })
                            )
                        }
                    })
                },
                Icon: Icons.LayerUnlocked,
            })
        } else {
            arr.push({
                title: "Lock",
                onClick: () => {
                    selectedLayers.forEach((layer) => {
                        if (layer.type) {
                            dispatch(
                                getSidePanelLayerTypeToUpdateFunctionMap()[layer.type]({
                                    id: layer.id,
                                    updates: {
                                        locked: true,
                                    },
                                })
                            )
                        }
                    })
                },
                Icon: Icons.LayerLocked,
            })
        }
        return arr
    }, [dispatch, selectedLayers])
    return mapActions
}

const allowedDownloadTypes = new Set<SidePanelLayerType>([
    SidePanelLayerType.Icon,
    SidePanelLayerType.Line,
    SidePanelLayerType.Ruler,
])

export function useExportSelectedShapes() {
    const dispatch = useAppDispatch()
    const selectedLayers = useAppSelector(selectSelectedLayers)
    const currentCanDownload = useAppSelector(selectCanDownload())
    const download = useCallback(() => {
        dispatch(initializeCenterModalForComponentAction(ModalNames.ExportLayersModal))
        dispatch(showGlobalModal())
    }, [dispatch])
    const downloadableLayers = useMemo(
        () => selectedLayers.filter((layer) => layer.type && allowedDownloadTypes.has(layer.type)),
        [selectedLayers]
    )
    const canDownload = downloadableLayers.length > 0
    useEffect(() => {
        if (canDownload !== currentCanDownload) dispatch(setCanDownload(canDownload))
    }, [canDownload, dispatch, currentCanDownload])
    return {
        canDownload,
        download,
    }
}
